/**
 * Biểu đồ của màn Sự cố, dựng trên bộ SVG thuần ở `nhat-ky-truy-vet/chung/components/BieuDo.tsx` (kho không có thư
 * viện biểu đồ, CLAUDE.md cấm thêm). Native dùng `components/charts.tsx` riêng; ở đây chỉ bọc dữ liệu cho bộ có sẵn.
 * Màu theo nguồn và theo mức độ cố định ở `constants.ts`.
 */
import { Empty } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import { BieuDoVanh, BieuDoXepChong, ChuGiai, ThanhNgang } from '../../../nhat-ky-truy-vet/chung/components/BieuDo';
import { MAU_MUC_DO, MAU_PHAN_BO, MUC_DO_LOI, NGUON_SU_CO_HIEN } from '../../constants';
import { mucDoSuCo, MUC_DO_SU_CO, NGUON_SU_CO, type CotNguon } from '../../nghiep-vu/su-co';
import type { SuCo } from '../../types';

function useLoat() {
  const { t } = useT();
  return NGUON_SU_CO.map((n) => ({ nhan: t(NGUON_SU_CO_HIEN[n].khoa), mau: NGUON_SU_CO_HIEN[n].mau }));
}

/** Đếm theo một nhãn: thanh ngang một màu, giảm dần; khoảng 5 dòng đầu, còn lại cuộn trong ô. */
export function PhanBo({ muc, nhan, rong }: { muc: { nhan: string; so: number }[]; nhan: string; rong: string }) {
  const { locale } = useT();
  if (!muc.length) return <Empty title={rong} className="py-6" />;
  const lonNhat = muc[0]!.so;
  return (
    <ul tabIndex={0} aria-label={nhan} className="max-h-44 space-y-2.5 overflow-y-auto overscroll-contain pr-2">
      {muc.map((m) => (
        <li key={m.nhan} className="text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-content-secondary" title={m.nhan}>
              {m.nhan}
            </span>
            <b className="shrink-0 tabular-nums text-content-primary">{soVN(m.so, locale)}</b>
          </div>
          <ThanhNgang phan={m.so} tren={lonNhat} mau={MAU_PHAN_BO} />
        </li>
      ))}
    </ul>
  );
}

export function VanhMucDo({ ds, nhan, rong }: { ds: readonly SuCo[]; nhan: string; rong: string }) {
  const { t, locale } = useT();
  if (!ds.length) return <Empty title={rong} className="py-6" />;
  const dem = MUC_DO_SU_CO.map((m) => ({ m, so: ds.filter((r) => mucDoSuCo(r) === m).length }));
  return (
    <div className="flex flex-wrap items-center gap-6">
      <BieuDoVanh
        nhan={nhan}
        giua={soVN(ds.length, locale)}
        phu={t('rpaSuCo.suCoGiua')}
        dinhDang={(gt) => t('rpaSuCo.soSuCoThe', { so: soVN(gt, locale) })}
        lat={dem.map(({ m, so }) => ({ nhan: t(MUC_DO_LOI[m].khoa), giaTri: so, mau: MAU_MUC_DO[m] }))}
      />
      <ul className="min-w-40 flex-1 space-y-2 text-sm">
        {dem.map(({ m, so }) => (
          <li key={m} className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 text-content-secondary">
              <span aria-hidden className="inline-block size-2.5 rounded-sm" style={{ backgroundColor: MAU_MUC_DO[m] }} />
              {t(MUC_DO_LOI[m].khoa)}
            </span>
            <b className="tabular-nums text-content-primary">
              {soVN(so, locale)}{' '}
              <span className="text-xs font-normal text-content-muted">
                ({soVN(Math.round((so / ds.length) * 100), locale)}%)
              </span>
            </b>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Cột xếp chồng theo nguồn (theo ngày, theo giờ). `tieuDeCot` cho trình đọc màn hình và thẻ chú thích. */
export function CotTheoNguon({
  cot,
  nhan,
  rong,
  tieuDeCot,
}: {
  cot: CotNguon[];
  nhan: string;
  rong: string;
  tieuDeCot: (c: CotNguon) => string;
}) {
  const { t, locale } = useT();
  const loat = useLoat();
  if (cot.every((c) => c.lat.every((v) => v === 0))) return <Empty title={rong} className="py-6" />;
  return (
    <>
      <BieuDoXepChong
        nhan={nhan}
        nhanTong={t('rpaSuCo.tong')}
        loat={loat}
        dinhDang={(gt) => t('rpaSuCo.soSuCoThe', { so: soVN(gt, locale) })}
        cot={cot.map((c) => {
          const tong = c.lat.reduce((a, v) => a + v, 0);
          return { nhan: c.nhan, tieuDe: tieuDeCot(c), lat: c.lat, tong, moTa: t('rpaSuCo.moTaCot', { cot: tieuDeCot(c), so: soVN(tong, locale) }) };
        })}
      />
      <ChuGiai muc={loat} />
    </>
  );
}

/** Thanh ngang xếp chồng theo nguồn cho từng Project; dài theo Project nhiều sự cố nhất. */
export function ThanhTheoProject({ dong, nhan, rong }: { dong: CotNguon[]; nhan: string; rong: string }) {
  const { t, locale } = useT();
  const loat = useLoat();
  if (!dong.length) return <Empty title={rong} className="py-6" />;
  const tong = (d: CotNguon) => d.lat.reduce((a, v) => a + v, 0);
  const lonNhat = Math.max(1, ...dong.map(tong));
  return (
    <>
      <ul aria-label={nhan} className="space-y-3">
        {dong.map((d) => (
          <li key={d.khoa} className="text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-content-secondary" title={d.nhan}>
                {d.nhan}
              </span>
              <b className="shrink-0 tabular-nums text-content-primary">{soVN(tong(d), locale)}</b>
            </div>
            <span
              role="img"
              aria-label={t('rpaSuCo.moTaProject', {
                project: d.nhan,
                chiTiet: d.lat.map((v, i) => `${loat[i]!.nhan} ${soVN(v, locale)}`).join(', '),
              })}
              className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-subtle"
              style={{ width: `${Math.max((tong(d) / lonNhat) * 100, 2)}%` }}
            >
              {d.lat.map((v, i) =>
                v > 0 ? (
                  <span key={i} className="block h-full" style={{ width: `${(v / tong(d)) * 100}%`, backgroundColor: loat[i]!.mau }} />
                ) : null,
              )}
            </span>
          </li>
        ))}
      </ul>
      <ChuGiai muc={loat} />
    </>
  );
}
